/**
 * CONFIRMACIÓN MULTI-OT BASADA EN ASIGNACIONES — FASE 4.3-E (UX corregida
 * en el UAT de FASE 4.3, BUG 3), MODAL OPERATIVO DESDE 4.3-F
 *
 * Selección de varias OT (en `PaginaOtsGlobal`) → revisión de sus líneas
 * confirmables → confirmar cada línea con SUS operarios asignados —
 * nunca con un único ejecutor común para todo el lote.
 *
 * `ASIGNADO ≠ EJECUTOR REAL` se mantiene entero, pero la propuesta
 * inicial YA es el asignado de CADA línea (misma filosofía que el
 * formulario unitario y el bulk por-OT): al abrir esta pantalla, cada
 * línea aparece preseleccionada con SUS asignados, nunca con la de otra
 * línea ni con un ejecutor común. El Supervisor/Consultor puede quitar,
 * añadir o corregir cada una antes de enviar; sin asignados, no se
 * inventa ejecutor. (Diseño previo: 4.3-E exigía un clic explícito
 * — "Aplicar asignaciones a todas" — antes de proponer nada; el UAT
 * encontró que eso obligaba a repetir manualmente, línea a línea, algo
 * que ya se sabía de antemano por línea.)
 *
 * El payload sigue siendo, línea a línea, el mismo contrato de FASE
 * 4.2-C (`POST /api/ot-lineas/confirmar-masivo`): `idLinea`,
 * `ejecutoresReales` propios, `fechaEfectiva`, `observacion`,
 * `idOperacionCliente` propio — una entrada por línea, autocontenida,
 * sin que el hecho de venir de varias OT distintas cambie nada del
 * contrato. El backend sigue siendo la única autoridad: revalida cada
 * línea (ámbito, estado, idempotencia) con independencia de qué OT
 * seleccionara el supervisor.
 *
 * Una línea con `ejecutoresReales.length === 0` nunca se envía —se
 * excluye del lote y se marca «Pendiente»— nunca se inventa un
 * ejecutor para poder enviarla.
 *
 * FASE 4.3-F — UAT de cierre: el panel que se renderizaba al final del
 * listado de `/ots` era poco usable (obligaba a desplazarse hasta el
 * final para ver lo que se acababa de pedir). Ahora es un `<Modal>`
 * (`componentes/base.tsx`): cabecera y acciones finales fijas, cuerpo
 * con scroll propio, agrupado visualmente por OT, con fecha
 * efectiva/observación COMUNES opcionales (un clic explícito las copia
 * a todas las líneas; cada línea sigue editable individualmente
 * después). El contrato con el backend no cambia un ápice.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  api,
  type ItemConfirmarMasivo,
  type OperarioDeLinea,
  type OperarioResumen,
  type OtDetalle,
  type RechazoLinea,
  type ResultadoAccionMasiva,
} from '../api.ts';
import {
  ahoraParaInput,
  Cargando,
  Cifra,
  Error as ErrorCaja,
  EstadoLinea,
  Modal,
  ResumenSeleccionMultiOt,
  useDatos,
  Vacio,
} from './base.tsx';

interface FilaMultiOt {
  readonly idOt: string;
  readonly otCodigo: string;
  readonly idCentro: string;
  readonly idLinea: string;
  readonly ubicacionCodigo: string;
  readonly ubicacionRuta: string;
  readonly descripcionTarea: string | null;
  readonly tipoDescripcion: string;
  readonly estadoLinea: string;
  readonly asignados: readonly OperarioDeLinea[];
  /** Generado UNA vez al construir la fila — estable durante toda la revisión, incluidos los reintentos. */
  readonly idOperacionCliente: string;
  ejecutoresReales: string[];
  fechaEfectiva: string;
  observacion: string;
}

interface CargaMultiOt {
  readonly ots: readonly OtDetalle[];
  readonly operariosPorCentro: ReadonlyMap<string, readonly OperarioResumen[]>;
}

async function cargarMultiOt(idsOt: readonly string[]): Promise<CargaMultiOt> {
  const ots = await Promise.all(idsOt.map((id) => api.get<OtDetalle>(`/api/ots/${id}`)));
  const idsCentro = [...new Set(ots.map((o) => o.idCentro))];
  const pares = await Promise.all(
    idsCentro.map(async (idCentro): Promise<readonly [string, readonly OperarioResumen[]]> => {
      const r = await api.get<{ operarios: OperarioResumen[] }>(`/api/operarios?idCentro=${idCentro}&activo=true&dominio=LIMPIEZA`);
      return [idCentro, r.operarios];
    }),
  );
  return { ots, operariosPorCentro: new Map(pares) };
}

export function RevisionMultiOt({
  idsOt,
  cerrar,
  alTerminar,
}: {
  idsOt: readonly string[];
  cerrar: () => void;
  alTerminar: () => void;
}): ReactNode {
  const carga = useDatos(() => cargarMultiOt(idsOt), [idsOt.join(',')]);
  const [filas, setFilas] = useState<FilaMultiOt[] | null>(null);
  const [etiquetas, setEtiquetas] = useState<Readonly<Record<string, { otCodigo: string; ubicacionCodigo: string }>>>(
    {},
  );
  const [fechaComun, setFechaComun] = useState('');
  const [observacionComun, setObservacionComun] = useState('');
  const [resultado, setResultado] = useState<ResultadoAccionMasiva | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  //  Deriva las filas editables UNA sola vez, en cuanto llegan las OT —
  //  no se recalculan en cada render (perdería lo que el supervisor ya
  //  hubiera editado).
  useEffect(() => {
    if (carga.datos === null || filas !== null) return;
    const iniciales: FilaMultiOt[] = [];
    const nuevasEtiquetas: Record<string, { otCodigo: string; ubicacionCodigo: string }> = {};
    for (const ot of carga.datos.ots) {
      for (const l of ot.lineas) {
        if (!l.accionesPermitidas.confirmar) continue;
        iniciales.push({
          idOt: ot.id,
          otCodigo: ot.codigo,
          idCentro: ot.idCentro,
          idLinea: l.id,
          ubicacionCodigo: l.ubicacionCodigo,
          ubicacionRuta: l.ubicacionRuta,
          descripcionTarea: l.descripcionTarea,
          tipoDescripcion: l.tipoDescripcion,
          estadoLinea: l.estado,
          asignados: l.asignados,
          idOperacionCliente: crypto.randomUUID(),
          //  BUG 3: propuesta inicial = asignados de ESTA línea. Nunca los
          //  de otra línea, nunca un ejecutor común para el lote entero.
          ejecutoresReales: l.asignados.map((a) => a.idOperario),
          fechaEfectiva: ahoraParaInput(),
          observacion: '',
        });
        nuevasEtiquetas[l.id] = { otCodigo: ot.codigo, ubicacionCodigo: l.ubicacionCodigo };
      }
    }
    setFilas(iniciales);
    setEtiquetas(nuevasEtiquetas);
  }, [carga.datos, filas]);

  function actualizarFila(idLinea: string, cambios: Partial<FilaMultiOt>): void {
    setFilas((prev) => (prev === null ? null : prev.map((f) => (f.idLinea === idLinea ? { ...f, ...cambios } : f))));
  }

  //  Fecha/observación COMUNES (L99 4.3-F, punto 2): un clic explícito
  //  las copia a TODAS las líneas todavía visibles. Cada línea sigue
  //  editable individualmente después — esto es una propuesta de
  //  partida, no un valor bloqueado.
  function aplicarComunes(): void {
    setFilas((prev) =>
      prev === null
        ? null
        : prev.map((f) => ({
            ...f,
            fechaEfectiva: fechaComun !== '' ? fechaComun : f.fechaEfectiva,
            observacion: observacionComun.trim() !== '' ? observacionComun : f.observacion,
          })),
    );
  }

  const listasParaEnviar = (filas ?? []).filter((f) => f.ejecutoresReales.length > 0);
  const pendientes = (filas?.length ?? 0) - listasParaEnviar.length;

  //  UX-POLISH-01 punto 5: las líneas no elegibles nunca desaparecen en
  //  silencio — se cuentan y se agrupan por estado, aunque sólo `filas`
  //  (las CONFIRMABLES) se editen y se envíen.
  const lineasNoElegibles = (carga.datos?.ots ?? [])
    .flatMap((ot) => ot.lineas)
    .filter((l) => !l.accionesPermitidas.confirmar);

  async function enviar(): Promise<void> {
    if (listasParaEnviar.length === 0) {
      setError(new globalThis.Error('Ninguna línea tiene ejecutores reales marcados todavía.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const lineas: ItemConfirmarMasivo[] = listasParaEnviar.map((f) => ({
        idLinea: f.idLinea,
        ejecutoresReales: f.ejecutoresReales,
        fechaEfectiva: new Date(f.fechaEfectiva).toISOString(),
        observacion: f.observacion.trim() === '' ? null : f.observacion.trim(),
        idOperacionCliente: f.idOperacionCliente,
      }));
      const r = await api.post<ResultadoAccionMasiva>('/api/ot-lineas/confirmar-masivo', { lineas });
      setResultado(r);
      const idsOk = new Set(r.procesadas.map((p) => p.idLinea));
      setFilas((prev) => (prev ?? []).filter((f) => !idsOk.has(f.idLinea)));
      alTerminar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={`Confirmar líneas de ${String(idsOt.length)} OT seleccionada(s)`}
      cerrar={cerrar}
      pie={
        filas !== null &&
        filas.length > 0 && (
          <>
            {pendientes > 0 && (
              <span className="pequena suave">{pendientes} línea(s) sin ejecutor — no se enviarán</span>
            )}
            <span className="modal-pie-relleno" />
            {error !== null && <ErrorCaja error={error} />}
            <button
              type="button"
              className="principal"
              disabled={enviando || listasParaEnviar.length === 0}
              onClick={() => void enviar()}
            >
              {enviando ? 'Enviando…' : `Confirmar ${String(listasParaEnviar.length)} línea(s)`}
            </button>
          </>
        )
      }
    >
      {carga.cargando && carga.datos === null && <Cargando texto="Cargando líneas de las OT seleccionadas…" />}
      {carga.error !== null && <ErrorCaja error={carga.error} reintentar={carga.recargar} />}

      {filas !== null &&
        (filas.length === 0 ? (
          resultado === null && <Vacio titulo="No quedan líneas pendientes de confirmar en las OT seleccionadas" />
        ) : (
          <>
            <ResumenSeleccionMultiOt
              otsSeleccionadas={idsOt.length}
              otsAfectadas={new Set(filas.map((f) => f.idOt)).size}
              lineasElegibles={filas.length}
              lineasNoElegibles={lineasNoElegibles}
              etiquetaAccion="confirmables"
            />
            <p className="pequena suave">
              Cada línea aparece preseleccionada con SUS operarios asignados, como propuesta — pero{' '}
              <strong>asignado ≠ ejecutor real</strong>: quita, añade o corrige antes de enviar. Las líneas sin
              asignados quedan sin ejecutor hasta que lo elijas a mano.
            </p>

            <div className="modal-comunes">
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor="confirmar-fecha-comun">Fecha efectiva común (opcional)</label>
                <input
                  id="confirmar-fecha-comun"
                  type="datetime-local"
                  value={fechaComun}
                  max={ahoraParaInput()}
                  onChange={(e) => setFechaComun(e.target.value)}
                />
              </div>
              <div className="campo" style={{ margin: 0, flex: 1, minWidth: 200 }}>
                <label htmlFor="confirmar-observacion-comun">Observación común (opcional)</label>
                <input
                  id="confirmar-observacion-comun"
                  type="text"
                  value={observacionComun}
                  onChange={(e) => setObservacionComun(e.target.value)}
                  placeholder="Se copia a todas las líneas al aplicar"
                />
              </div>
              <button
                type="button"
                className="pequeno"
                disabled={fechaComun === '' && observacionComun.trim() === ''}
                onClick={aplicarComunes}
              >
                Aplicar a todas las líneas
              </button>
            </div>

            {carga.datos?.ots.map((ot) => {
              const filasDeEstaOt = filas.filter((f) => f.idOt === ot.id);
              if (filasDeEstaOt.length === 0) return null;
              return (
                <div key={ot.id} className="modal-grupo-ot">
                  <div className="modal-grupo-ot-cabecera">
                    <Link to={`/ots/${ot.id}`} className="mono">
                      {ot.codigo}
                    </Link>
                    <span className="suave pequena">{ot.centroDescripcion}</span>
                    <span className="pequena tenue">
                      {filasDeEstaOt.length} línea{filasDeEstaOt.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="lote-bulk-filas">
                    {filasDeEstaOt.map((f) => (
                      <FilaMultiOtEditable
                        key={f.idLinea}
                        fila={f}
                        operarios={carga.datos?.operariosPorCentro.get(f.idCentro) ?? []}
                        onCambiar={(cambios) => actualizarFila(f.idLinea, cambios)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        ))}

      {resultado !== null && (
        <ResultadoMultiOtPanel resultado={resultado} etiquetas={etiquetas} cerrar={() => setResultado(null)} />
      )}
    </Modal>
  );
}

function FilaMultiOtEditable({
  fila,
  operarios,
  onCambiar,
}: {
  fila: FilaMultiOt;
  operarios: readonly OperarioResumen[];
  onCambiar: (cambios: Partial<FilaMultiOt>) => void;
}): ReactNode {
  const sinEjecutor = fila.ejecutoresReales.length === 0;

  function alternarEjecutor(id: string): void {
    onCambiar({
      ejecutoresReales: fila.ejecutoresReales.includes(id)
        ? fila.ejecutoresReales.filter((x) => x !== id)
        : [...fila.ejecutoresReales, id],
    });
  }

  return (
    <div className={`lote-bulk-fila${sinEjecutor ? ' pendiente' : ''}`}>
      <div className="lote-bulk-fila-cabecera" style={{ flexWrap: 'wrap' }}>
        <span className="cod">{fila.ubicacionCodigo}</span>
        <span className="pequena suave">{fila.descripcionTarea ?? fila.tipoDescripcion}</span>
        <EstadoLinea estado={fila.estadoLinea} />
        {sinEjecutor && <span className="etiqueta aviso">Pendiente — falta ejecutor</span>}
      </div>
      <div className="pequena tenue" style={{ marginBottom: 8 }}>
        Asignado{fila.asignados.length === 1 ? '' : 's'}:{' '}
        {fila.asignados.length === 0 ? 'ninguno' : fila.asignados.map((a) => a.codigo).join(', ')}
      </div>
      <div className="campo" style={{ marginBottom: 8 }}>
        <span className="etiqueta-dato">Ejecutores reales</span>
        {operarios.length === 0 ? (
          <p className="suave pequena">No hay operarios habilitados en este centro.</p>
        ) : (
          <div className="lista-checks lista-checks-horizontal">
            {operarios.map((op) => (
              <label key={op.id} className="fila" style={{ gap: 4 }}>
                <input
                  type="checkbox"
                  checked={fila.ejecutoresReales.includes(op.id)}
                  onChange={() => alternarEjecutor(op.id)}
                />
                <span className="pequena">{op.codigo}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="fila" style={{ gap: 10, flexWrap: 'wrap' }}>
        <input
          type="datetime-local"
          aria-label={`Fecha efectiva — ${fila.otCodigo} ${fila.ubicacionCodigo}`}
          value={fila.fechaEfectiva}
          max={ahoraParaInput()}
          onChange={(e) => onCambiar({ fechaEfectiva: e.target.value })}
        />
        <input
          type="text"
          aria-label={`Observación — ${fila.otCodigo} ${fila.ubicacionCodigo}`}
          placeholder="Observación (opcional)"
          value={fila.observacion}
          onChange={(e) => onCambiar({ observacion: e.target.value })}
          style={{ flex: 1, minWidth: 160 }}
        />
      </div>
    </div>
  );
}

function ResultadoMultiOtPanel({
  resultado,
  etiquetas,
  cerrar,
}: {
  resultado: ResultadoAccionMasiva;
  etiquetas: Readonly<Record<string, { otCodigo: string; ubicacionCodigo: string }>>;
  cerrar: () => void;
}): ReactNode {
  const nuevas = resultado.procesadas.filter((p) => !p.yaEstaba).length;
  const yaEstaban = resultado.procesadas.filter((p) => p.yaEstaba).length;

  function etiquetaDe(r: RechazoLinea): { otCodigo: string; ubicacionCodigo: string } {
    return etiquetas[r.idLinea] ?? { otCodigo: '—', ubicacionCodigo: r.idLinea };
  }

  return (
    <div className="panel resultado-bulk" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h3>Resultado — confirmar en lote</h3>
        <button type="button" className="pequeno" onClick={cerrar}>
          Cerrar
        </button>
      </div>
      <div className="panel-cuerpo">
        <div className="cifras">
          <Cifra n={nuevas} t="procesadas" />
          <Cifra n={yaEstaban} t="ya estaban realizadas" />
          <Cifra n={resultado.rechazadas.length} t="rechazadas" />
        </div>
        {resultado.rechazadas.length > 0 && (
          <div className="sep">
            <p className="pequena suave">Líneas rechazadas — el servidor revalidó cada una por separado:</p>
            <ul className="lista-rechazadas">
              {resultado.rechazadas.map((r) => {
                const et = etiquetaDe(r);
                return (
                  <li key={r.idLinea}>
                    <div className="fila" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="mono pequena">{et.otCodigo}</span>
                      <span className="cod pequena">{et.ubicacionCodigo}</span>
                      <span className="etiqueta error pequena">{r.codigo}</span>
                    </div>
                    <div className="pequena suave">{r.mensaje}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
